/**
 * Client side CSV export of what is on screen.
 *
 * The old export queued a server job and handed back a reference number, which meant the
 * operator had to go and find the file. What people actually want is the rows they are
 * looking at, right now, with the same headers the table shows. That is a Blob and a click.
 *
 * Nothing here recomputes a figure. Money is divided by 100 for the spreadsheet and that is
 * the whole of the arithmetic.
 *
 * A packer's rows carry no money (CONTRACTS.md 3.6), and her file has no money columns: not
 * empty cells under a "Cost (WAC)" header, no header at all. The same rule the table follows.
 */
import { FAMILY_LABEL } from './constants';
import { rowHasMoney, type AnyStockPageRow } from './types';

/** The header row, in the order the table renders its columns. */
export const CSV_HEADERS = [
  'Family',
  'Product',
  'Size',
  'SKU',
  'In stock',
  'Inbound',
  '30-day units',
  '30-day revenue',
  'Days left',
  'Status',
  'Sale price',
  'Inv value',
  'Cost (WAC)',
  'WAC source',
  'Margin %',
  'Best price',
  'Best price supplier',
  'Negotiated',
] as const;

/** The headers a packer's export carries: `CSV_HEADERS` less every money column. */
export const CSV_HEADERS_PACKER = [
  'Family',
  'Product',
  'Size',
  'SKU',
  'In stock',
  'Inbound',
  '30-day units',
  'Days left',
  'Status',
] as const;

export function csvHeadersFor(rows: readonly AnyStockPageRow[]): readonly string[] {
  return rows.some(rowHasMoney) ? CSV_HEADERS : CSV_HEADERS_PACKER;
}

/**
 * The quoting and the download moved to `@/lib/csv` when Orders started exporting too.
 * They are re-exported here so nothing that imported them from this file had to change.
 */
export { downloadCsv } from '@/lib/csv';
import { csvCell as cell, dollars } from '@/lib/csv';

export function rowsToCsv(rows: readonly AnyStockPageRow[]): string {
  const withMoney = rows.some(rowHasMoney);
  const lines: string[] = [csvHeadersFor(rows).map(cell).join(',')];
  for (const row of rows) {
    const fields: (string | number)[] = withMoney && rowHasMoney(row)
      ? [
          FAMILY_LABEL[row.family],
          row.name,
          row.size_label ?? '',
          row.sku ?? '',
          row.on_hand,
          row.inbound_qty,
          row.units_30d,
          dollars(row.revenue_30d_cents),
          row.days_left === null ? 'no sales 30d' : row.days_left,
          row.status,
          dollars(row.price_cents),
          dollars(row.inv_value_cents),
          dollars(row.wac_cents),
          row.wac_source,
          row.margin_pct === null ? '' : (row.margin_pct * 100).toFixed(1),
          dollars(row.best_price_cents),
          row.best_price_supplier ?? '',
          row.best_price_negotiated ? 'yes' : 'no',
        ]
      : [
          FAMILY_LABEL[row.family],
          row.name,
          row.size_label ?? '',
          row.sku ?? '',
          row.on_hand,
          row.inbound_qty,
          row.units_30d,
          row.days_left === null ? 'no sales 30d' : row.days_left,
          row.status,
        ];
    lines.push(fields.map(cell).join(','));
  }
  // A trailing newline, because a file without one confuses half the tools that read CSV.
  return `${lines.join('\r\n')}\r\n`;
}
